import {
  Alert, Box, Button, Checkbox, Grid2, ListItemText, MenuItem, Skeleton, Typography,
} from '@mui/material';
import { FormState, useFormState, useSendEmail } from '@lib';
import { Formik } from 'formik';
import Link from '@components/atoms/Link';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Select from '@components/atoms/Select';
import TextInput from '@components/atoms/TextInput';
import { gql, useLazyQuery } from '@apollo/client';
import { formikInjectedPropsTypes } from '@forms/propTypes';
import { trackEvent } from '@components/organisms/Tracking';
import { useTranslation } from 'next-i18next';
import FORM_INITIAL_VALUES from './initialValues';
import validationSchema from './validate';
import { generateXml } from '@/lib/utils/dealerdesk';

export default function ModelRequestForm(props) {
  const {
    message,
    formFields,
    branch,
    vehicle,
  } = props;
  const { t } = useTranslation();

  const [getBranches, {
    error,
    data: branchesData,
    loading,
  }] = useLazyQuery(FETCH_BRANCHES);

  useEffect(() => {
    if (!branch) {
      getBranches({
        notifyOnNetworkStatusChange: true,
      });
    }
  }, [branch, getBranches]);

  if (error) {
    throw error;
  }

  const {
    formState,
    handleGraphQlResult,
    resetFormState,
  } = useFormState();
  const sendEmail = useSendEmail();

  const handleSubmit = async (values) => {
    try {
      const { create } = await import('xmlbuilder2');
      const xmlDoc = generateXml(create, values, vehicle);

      let data = {
        to: values.branchEmail,
        bcc: ['info@dekay.dev', 'katrin.goerlitz@schimmel-automobile.de'],
        templateId: 'd-10b9b7071c3548c188bfe2516846dbec',
        dynamicTemplateData: {
          ...values,
          url: window.location.href,
          requestTestDrive: values.newField.includes('requestTestDrive') ? 'Ja' : 'Nein',
          requestFinancingOffer: values.newField.includes('requestFinancingOffer') ? 'Ja' : 'Nein',
          requestLeasingOffer: values.newField.includes('requestLeasingOffer') ? 'Ja' : 'Nein',
          requestCarSubscription: values.newField.includes('requestCarSubscription') ? 'Ja' : 'Nein',
          requestTradeInOffer: values.newField.includes('requestTradeInOffer') ? 'Ja' : 'Nein',
        },
        attachments: [
          {
            content: window.btoa(xmlDoc.toString()),
            filename: 'dealerdesk.xml',
            type: 'application/xml',
            disposition: 'attachment',
          },
        ],
      };

      let result = await sendEmail(data);

      data = {
        to: values.email,
        bcc: ['info@dekay.dev', 'katrin.goerlitz@schimmel-automobile.de'],
        templateId: 'd-950951649ce1442c964c788a928c2e42',
        dynamicTemplateData: {
          ...values,
          url: window.location.href,
          requestTestDrive: values.newField.includes('requestTestDrive') ? 'Ja' : 'Nein',
          requestFinancingOffer: values.newField.includes('requestFinancingOffer') ? 'Ja' : 'Nein',
          requestLeasingOffer: values.newField.includes('requestLeasingOffer') ? 'Ja' : 'Nein',
          requestCarSubscription: values.newField.includes('requestCarSubscription') ? 'Ja' : 'Nein',
          requestTradeInOffer: values.newField.includes('requestTradeInOffer') ? 'Ja' : 'Nein',
        },
      };

      result = await sendEmail(data);
      handleGraphQlResult(result);

      trackEvent('Lead', { content_name: 'Fahrzeuganfrage' });
    } catch (e) {
      console.error(e);
    }
  };

  switch (formState) {
    case FormState.INITIAL:
      return (
        <Formik
          initialValues={{
            ...FORM_INITIAL_VALUES,
            message: message ?? '',
            branchEmail: branch?.attributes.primaryEmail ?? '',
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {(formikBag) => (
            <Form
              branch={branch}
              branches={branchesData?.branches.data ?? []}
              formFields={formFields}
              isCarSubscriptionAvailable={Boolean(vehicle?.attributes.isCarSubscription)}
              isDummarCar={Boolean(vehicle?.attributes.isDummyVehicle)}
              loading={loading}
              t={t}
              {...formikBag}
            />
          )}
        </Formik>
      );
    case FormState.SUCCESS:
      return (
        <Box my={3}>
          <Alert
            action={(
              <Button onClick={resetFormState}>
                {t('forms.ModelRequestForm.successAlert.newRequest')}
              </Button>
            )}
            severity="success"
          >
            {t('forms.ModelRequestForm.successAlert.content')}
          </Alert>
        </Box>
      );
    case FormState.ERROR:
      return (
        <div>
          Error
        </div>
      );
    default: {
      return null;
    }
  }
}

ModelRequestForm.propTypes = {
  message: PropTypes.string,
  formFields: PropTypes.object,
};

function Form(props) {
  const {
    t,
    formFields,
    branches,
    branch,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    dirty,
    errors,
    touched,
    isValid,
    loading,
    isSubmitting,
    isDummarCar,
    isCarSubscriptionAvailable,
  } = props;

  useEffect(() => {
    const handleRequestDrive = (event) => {
      setFieldValue('newField', ['requestTestDrive']);
    };

    window.addEventListener('requestTestDrive', handleRequestDrive);

    return () => {
      window.removeEventListener('requestTestDrive', handleRequestDrive);
    };
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      {loading && !values.branchEmail ? (
        <Box my={3}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Box>
      ) : (
        <>
          <Box my={3}>
            <Grid2 container spacing={5}>
              <Grid2 size={{
                md: 5,
                xs: 12,
              }}
              >
                <Box>
                  <Select
                    label={t('forms.ModelRequestForm.selectLocation')}
                    onChange={(e) => setFieldValue('branchEmail', e.target.value)}
                    readOnly={Boolean(branch)}
                    sx={branch ? { pointerEvents: 'none' } : null}
                    value={values.branchEmail}
                  >
                    {([...branches, branch]).map((item) => (
                      <MenuItem key={item?.id} value={item?.attributes.primaryEmail}>
                        {item?.attributes.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box mt={4}>
                  <Select
                    label={t('forms.ModelRequestForm.salutation')}
                    onChange={(e) => setFieldValue('salutation', e.target.value)}
                    value={values.salutation}
                  >
                    <MenuItem value="Frau">
                      {t('forms.ModelRequestForm.woman')}
                    </MenuItem>
                    <MenuItem value="Herr">
                      {t('forms.ModelRequestForm.mr')}
                    </MenuItem>
                  </Select>
                </Box>
                <Box mt={2}>
                  <TextInput
                    error={errors.first_name && touched.first_name}
                    fullWidth
                    helperText={errors.first_name}
                    label={t('forms.ModelRequestForm.firstName')}
                    name="first_name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size="small"
                    value={values.first_name}
                  />
                </Box>
                <Box mt={2}>
                  <TextInput
                    error={errors.last_name && touched.last_name}
                    fullWidth
                    helperText={errors.last_name}
                    label={t('forms.ModelRequestForm.lastName')}
                    name="last_name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size="small"
                    value={values.last_name}
                  />
                </Box>
                <Box mt={2}>
                  <TextInput
                    error={errors.email && touched.email}
                    fullWidth
                    helperText={errors.email}
                    label={t('forms.ModelRequestForm.email')}
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size="small"
                    value={values.email}
                  />
                </Box>
                <Box mt={2}>
                  <TextInput
                    error={errors.phone && touched.phone}
                    fullWidth
                    helperText={errors.phone}
                    label={t('forms.ModelRequestForm.phone')}
                    name="phone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size="small"
                    value={values.phone}
                  />
                </Box>
                {(formFields?.colors || formFields?.equipmentLines)
                  ? (
                    <>
                      {formFields.colors && (
                        <Box mt={4}>
                          <Select
                            label={t('forms.ModelRequestForm.color')}
                            onChange={(e) => setFieldValue('color', e.target.value)}
                            value={values.color}
                          >
                            {formFields.colors
                              .split('\n')
                              .map((value) => value.trim())
                              .map((value) => (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                          </Select>
                        </Box>
                      )}
                      {formFields.equipmentLines && (
                        <Box mt={4}>
                          <Select
                            label={t('forms.ModelRequestForm.equipment')}
                            onChange={(e) => setFieldValue('equipmentLine', e.target.value)}
                            value={values.equipmentLine}
                          >
                            {formFields.equipmentLines
                              .split('\n')
                              .map((value) => value.trim())
                              .map((value) => (
                                <MenuItem key={value} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                          </Select>
                        </Box>
                      )}
                    </>
                  ) : (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                      {(!isDummarCar || !isCarSubscriptionAvailable) && (
                        <Box mt={2}>
                          <Select
                            label={t('forms.ModelRequestForm.newOptions.label')}
                            multiple
                            onChange={(e) => setFieldValue('newField', e.target.value)}
                            renderValue={(selected) => NEW_FIELD_NAMES
                              .filter((field) => selected.indexOf(field.value) > -1)
                              .map((field) => t(`forms.ModelRequestForm.newOptions.${field.name}`))
                              .join(', ')}
                            value={values.newField}
                          >
                            {NEW_FIELD_NAMES
                              .filter((field) => (isCarSubscriptionAvailable ? true : field.value !== 'requestCarSubscription'))
                              .map((field) => (
                                <MenuItem key={field.name} value={field.value}>
                                  <Checkbox
                                    checked={values.newField.indexOf(field.value) > -1}
                                    color="primary"
                                  />
                                  <ListItemText primary={t(`forms.ModelRequestForm.newOptions.${field.name}`)} />
                                </MenuItem>
                              ))}
                          </Select>
                        </Box>
                      )}
                    </>
                  )}
              </Grid2>
              <Grid2 size={{
                md: 7,
                xs: 12,
              }}
              >
                <Box>
                  <TextInput
                    error={errors.message && touched.message}
                    fullWidth
                    helperText={errors.message}
                    label={t('forms.ModelRequestForm.message')}
                    multiline
                    name="message"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    rows={16}
                    size="small"
                    value={values.message}
                  />
                </Box>
                <Box mt={2}>
                  {/* <FormControlLabel
                label={
                  <Typography variant="caption">
                    Mit Art, Umfang und Zweck der Erhebung, Verwendung und
                    Berabeitung meiner personenbezogenen Daten gemäß der
                    Datenschutzerklärung erkläre ich mich einverstanden und willige darin ein.
                  </Typography>
                }
                control={
                  <Checkbox
                    color="primary"
                    checked={values.isTermsAndPrivacyAccepted}
                    onChange={(e) => setFieldValue('isTermsAndPrivacyAccepted', e.target.checked)}
                  />
                }
              /> */}
                  <Typography>
                    {t('forms.ModelRequestForm.dataProtectionText')}
                    {' '}
                    <Link href={t('urls.privacy-policy')} legacyBehavior>
                      <a target="_blank">
                        {t('forms.ModelRequestForm.dataProtectionLink')}
                      </a>
                    </Link>
                    {' '}
                    {t('forms.ModelRequestForm.dataProtectionText2')}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
          <Box display="flex" justifyContent="center" pt={3}>
            <Button
              color="primary"
              disabled={!dirty || !isValid || isSubmitting}
              fullWidth
              sx={{ maxWidth: 300 }}
              type="submit"
              variant="contained"
            >
              {t('forms.ModelRequestForm.sending')}
            </Button>
          </Box>
        </>
      )}
    </form>
  );
}

Form.propTypes = {
  ...formikInjectedPropsTypes,
};

const FETCH_BRANCHES = gql`
  query FetchBranches {
    branches(sort: "name:ASC") {
      data {
        id
        attributes {
          name
          primaryEmail
        }
      }
    }
  }
`;

const NEW_FIELD_NAMES = [
  {
    name: 'requestTestDrive',
    value: 'requestTestDrive',
  },
  {
    name: 'requestFinancingOffer',
    value: 'requestFinancingOffer',
  },
  {
    name: 'requestLeasingOffer',
    value: 'requestLeasingOffer',
  },
  {
    name: 'requestTradeInOffer',
    value: 'requestTradeInOffer',
  },
];
